import { useLang } from "../../context/LanguageContext";
import { VILLAGE, VILLAGE_IMAGE, VILLAGE_PHOTOS, ATTR_ICONS, EVENT_ICONS } from "../../i18n/village";
import { Reveal, SectionHeading } from "../Reveal";
import { Icon } from "../Icon";
import { CalendarDays } from "lucide-react";

export const Village = () => {
  const { lang } = useLang();
  const v = VILLAGE[lang] || VILLAGE.it;

  return (
    <section id="village" className="scroll-mt-20 bg-[#F3EFEA] px-4 sm:px-8 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <SectionHeading eyebrow={v.eyebrow} title={v.title} subtitle={v.subtitle} />
        </Reveal>

        {/* History */}
        <div className="mt-10 grid items-stretch gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-stone-200 shadow-sm">
              <img src={VILLAGE_IMAGE} alt={v.title} className="h-full min-h-[280px] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col justify-center rounded-3xl bg-white p-7 sm:p-9">
              <span className="font-serif text-2xl text-[#C85A32]">{v.badge}</span>
              <p className="mt-4 text-base leading-relaxed text-stone-700" data-testid="village-history">{v.history}</p>
            </div>
          </Reveal>
        </div>

        {/* Real photos strip */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {VILLAGE_PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={Math.min(i * 0.06, 0.25)}>
              <div className="group h-56 overflow-hidden rounded-2xl border border-stone-200 shadow-sm">
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </Reveal>
          ))}
        </div>

        {/* What to visit */}
        <Reveal>
          <h3 className="mt-16 font-serif text-2xl sm:text-3xl text-stone-900">{v.visitTitle}</h3>
        </Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {v.attractions.map((a, i) => (
            <Reveal key={a.name} delay={Math.min(i * 0.05, 0.3)}>
              <div
                data-testid={`attraction-${i}`}
                className="flex h-full flex-col gap-3 rounded-2xl border border-stone-200/80 bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#5F6B52]/12 text-[#5F6B52]">
                  <Icon name={ATTR_ICONS[i] || "MapPin"} className="h-5 w-5" />
                </span>
                <h4 className="font-serif text-xl text-stone-900">{a.name}</h4>
                <p className="text-sm leading-relaxed text-stone-600">{a.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Festivals */}
        <Reveal>
          <h3 className="mt-16 font-serif text-2xl sm:text-3xl text-stone-900">{v.eventsTitle}</h3>
        </Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {v.events.map((e, i) => (
            <Reveal key={e.name} delay={Math.min(i * 0.07, 0.3)}>
              <div
                data-testid={`event-${i}`}
                className="flex h-full flex-col gap-3 rounded-2xl bg-[#1C1917] p-6 text-stone-100"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D9A05B]/15 text-[#D9A05B]">
                  <Icon name={EVENT_ICONS[i] || "Calendar"} className="h-5 w-5" />
                </span>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#D9A05B]">
                  <CalendarDays className="h-3.5 w-3.5" /> {e.when}
                </div>
                <h4 className="font-serif text-xl text-white">{e.name}</h4>
                <p className="text-sm leading-relaxed text-stone-300">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
